import { DateTime, Duration } from "luxon";
import { action, computed, makeObservable, observable, reaction, runInAction, toJS } from "mobx";

import type { Uuid } from "@/domains/global/identifiers";
import { AudioRecordingError, type BaseError } from "@/domains/errors";
import { AudioRecordingRepository } from "@/domains/dexie-db/repositories/audio-recording/audio-recording";
import { LocalAudioRecordingTranscriptTurnsRepository } from "@/domains/dexie-db/repositories/audio-recording/local-audio-recording-transcript-turns";
import { errorHandlingModule } from "@/modules/error-handling";
import { logger } from "@/modules/logger";
import {
  MediaController,
  MediaControllerEventHandlers,
  MediaControllerInitializeOptions,
  MediaRecorderAudioChunk,
  MediaRecorderTranscriptFinalTurn,
  MediaRecorderTranscriptPartialTurn,
  MediaRecorderWaveformSamples,
  TranscriptionStatus,
} from "@/store/audio/recording/audio-recording.types";
import { IAudioRecordingStore } from "@/store/audio/recording/audio-recording.types";
import { AudioRecordingAutoStopManager } from "@/store/audio/recording/auto-stop";
import { AudioLevelProbe } from "@/modules/audio-recording/audio-level-probe";
import { AppStoreAudioUiStore } from "@/store/audio/ui/audio-ui.store";
import { DesktopAudioRecorderFailureReason } from "@/store/audio/ui/modals/system-audio-recording-failed-modal.types";
import { AppSubStore, type AppSubStoreArgs } from "@/store/types";
import {
  AudioRecordingSilenceDetectionManager,
  type SilenceSnapshot,
} from "@/store/audio/recording/silence-detection";
import { AudioRecordingRecord } from "@/domains/dexie-db/models/audio-recording/audio-recording";
import {
  DesktopPreparedRecordingController,
  PreparedRecording,
  PreparedRecordingController,
  WebPreparedRecordingController,
} from "@/store/audio/recording/prepared-recording-controller";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { objectModule } from "@/modules/object";
import { uuidModule } from "@/modules/uuid";
import { ensureSourceForRecording } from "@/store/audio-recordings/ensureSourceForRecording";
import { TrashRecordingReason } from "@/store/audio/audio.types";
import {
  DesktopAudioRecordingStrategy,
  DesktopRecordingRecoveryKind,
  DesktopRecordingRecoveryState,
  DesktopRecordingSessionState,
  type DesktopRecordingStartAttemptTrace,
  RecallPermissionKind,
  type RecallPermissionMap,
  type RecallSdkErrorDiagnostics,
} from "@/modules/electron/types";
import { electronModule } from "@/modules/electron";
import {
  AudioRecordingRecordingStatus,
  getAudioRecordingStrategy,
  AudioRecordingStrategy,
  normalizeAudioRecordingStrategy,
} from "@/store/audio-recordings/types";
import { calculateRecordingDuration } from "@/store/audio-recordings";
import {
  AudioRecordingStrategyDriver,
  DesktopArtifactRecordingStrategyDriver,
  WebArtifactRecordingStrategyDriver,
} from "@/store/audio/recording/strategies";
import {
  DesktopArtifactMediaController,
  DesktopMediaController,
  WebMediaController,
} from "@/store/audio/recording/media-controller";
import {
  buildDesktopRecordingStartFailureLogInfo,
  buildDesktopRecordingRestartRequiredLogInfo,
  buildDesktopRecordingStartAttemptLogInfo,
  type DesktopControllerKind,
  getDesktopRecordingStartFailureError,
  getDesktopRecordingStartFailureMetadata,
  type DesktopRecordingStartFailureMetadata,
  DesktopRecordingRestartRequiredError,
} from "@/store/audio/recording/media-controller/desktop-controller-errors";

type AudioRecordingState = {
  controller: MediaController;
  initialTranscriptTurnOrder: number;
  pendingAudioChunkPersistence: Promise<void>;
};

type RecordingStoreConfig = {
  onRecordingCompleted?: (args: { audioRecordingId: Uuid }) => void;
};

type AppStoreAudioRecordingStoreArgs = AppSubStoreArgs & {
  uiStore: AppStoreAudioUiStore;
  webArtifactStrategyDriver: WebArtifactRecordingStrategyDriver;
  desktopArtifactStrategyDriver: DesktopArtifactRecordingStrategyDriver;
  config?: RecordingStoreConfig;
};

type LiveDesktopRecordingRecoveryState = Extract<
  DesktopRecordingRecoveryState,
  { kind: DesktopRecordingRecoveryKind.LiveSession }
>;

const DESKTOP_RECORDER_START_FAILURE_REASON = "desktop_recorder_start_failed";
const WEB_ARTIFACT_READINESS_MAX_WAIT_MS = 500;
const WEB_ARTIFACT_READINESS_POLL_INTERVAL_MS = 25;

type RecordingErrorMetadata = {
  errorMessage?: string | null;
  errorName?: string | null;
  errorSource?: string | null;
  errorStage?: string | null;
  errorSummary?: string | null;
  errorDetails?: Record<string, unknown> | null;
  failureReason?: string | null;
  recordingStrategy?: AudioRecordingStrategy | null;
  forceWebMediaController?: boolean | null;
  wasPaused?: boolean | null;
};

type RecordingCompletionDebugInfo = {
  recordingStrategy: AudioRecordingStrategy;
  transcriptTurnCount: number;
  artifactSummary: Awaited<ReturnType<AudioRecordingStrategyDriver["getArtifactDebugSummary"]>>;
};

type DesktopStartLogContext = {
  audioRecordingId: Uuid;
  calendarEventId?: string;
  detectedMeetingWindowId?: string;
  noteId: Uuid | null;
  startAttemptTrace?: DesktopRecordingStartAttemptTrace;
};

type DesktopStartFallbackContext = DesktopStartLogContext & {
  controllerKind: DesktopControllerKind | null;
  forceWebMediaController: boolean;
  recordingStrategy: AudioRecordingStrategy;
  wasPaused: boolean;
};

const getRecordingCompletionUserAgent = (): string =>
  typeof navigator !== "undefined" && typeof navigator.userAgent === "string"
    ? navigator.userAgent
    : "unknown";

const EMPTY_LIVE_TRANSCRIPT_PARTIALS: MediaRecorderTranscriptPartialTurn[] = [];

export class AppStoreAudioRecordingStore extends AppSubStore implements IAudioRecordingStore {
  visualizer: AudioLevelProbe | null = null;
  waveformSamples: MediaRecorderWaveformSamples | null = null;
  isMicrophoneDisconnected = false;
  activeAudioInputDeviceLabel: string | null = null;
  isAudioSilent = false;
  audioSilentSince: number | null = null;
  transcriptIsUnsupported = false;

  readonly ui: AppStoreAudioUiStore;
  lastError: BaseError | null = null;
  isPaused = true;
  isInitializing = false;

  private readonly audioRecordingStateMap = new Map<Uuid, AudioRecordingState>();
  private readonly erroredRecordingIds = new Set<Uuid>();
  private readonly onRecordingCompleted: ((args: { audioRecordingId: Uuid }) => void) | null;
  private readonly autoStopManager: AudioRecordingAutoStopManager;
  private readonly silenceDetectionManager: AudioRecordingSilenceDetectionManager;
  private readonly transcriptionStatusByRecording = new Map<Uuid, TranscriptionStatus>();
  private readonly liveTranscriptPartialsByRecording = new Map<
    Uuid,
    MediaRecorderTranscriptPartialTurn[]
  >();
  private readonly liveDurationMsByRecording = new Map<Uuid, number>();
  private readonly silenceSnapshotByRecording = new Map<Uuid, SilenceSnapshot>();
  private readonly preparedRecordingController: PreparedRecordingController;
  private readonly webArtifactStrategyDriver: WebArtifactRecordingStrategyDriver;
  private readonly desktopArtifactStrategyDriver: DesktopArtifactRecordingStrategyDriver;
  private preparedRecording: PreparedRecording | null = null;
  private preparedRecordingTimeout = 0;

  constructor(injectedDeps: AppStoreAudioRecordingStoreArgs) {
    super(injectedDeps);

    this.ui = injectedDeps.uiStore;
    this.webArtifactStrategyDriver = injectedDeps.webArtifactStrategyDriver;
    this.desktopArtifactStrategyDriver = injectedDeps.desktopArtifactStrategyDriver;
    this.onRecordingCompleted = injectedDeps.config?.onRecordingCompleted ?? null;

    makeObservable<
      this,
      | "audioRecordingStateMap"
      | "onRecordingCompleted"
      | "audioRecordings"
      | "localAudioRecordingTranscriptTurns"
      | "computeInitialIndexes"
      | "createPreparedRecordingController"
      | "buildControllerEventHandlers"
      | "trackAudioChunkPersistence"
      | "persistAudioChunk"
      | "persistTranscriptTurn"
      | "handleControllerError"
      | "buildRecordingCompletionDebugInfo"
      | "waitForUploadableArtifactReadiness"
      | "handleControllerStop"
      | "logRecordingCompletionSummary"
      | "disposeRecordingState"
      | "erroredRecordingIds"
      | "markRecordingAsErrored"
      | "buildRecordingErrorMetadata"
      | "normalizeRecordingErrorDetails"
      | "buildDesktopRecordingStartFailureMetadata"
      | "buildDesktopStartFailureLogInfo"
      | "handleDesktopStartFallback"
      | "retryDesktopStartAfterSdkRestart"
      | "resolveDesktopRecordingStartAttemptTrace"
      | "buildDesktopRecordingStartPermissionErrorDetails"
      | "buildDesktopRecordingStartFailureDetails"
      | "buildRecallSdkErrorDetails"
      | "createDesktopRecordingStartAttemptTrace"
      | "initializeStateForRecording"
      | "visualizer"
      | "waveformSamples"
      | "isMicrophoneDisconnected"
      | "activeAudioInputDeviceLabel"
      | "setMicrophoneState"
      | "isAudioSilent"
      | "audioSilentSince"
      | "setVisualizer"
      | "setTranscriptionUnsupported"
      | "setTranscriptionStatus"
      | "normalizeTranscriptTurnOrder"
      | "liveTranscriptPartialsByRecording"
      | "getLiveTranscriptPartials"
      | "upsertLiveTranscriptPartial"
      | "removeLiveTranscriptPartial"
      | "reconcileLiveTranscriptPartialsWithPersistedTurns"
      | "clearLiveTranscriptPartials"
      | "liveDurationMsByRecording"
      | "silenceSnapshotByRecording"
      | "getRecordingStrategyForRecording"
      | "getLiveDurationMs"
      | "setLiveDurationMs"
      | "updateLiveDurationFromWaveform"
      | "autoStopManager"
      | "silenceDetectionManager"
      | "transcriptionStatusByRecording"
      | "preparedRecordingController"
      | "webArtifactStrategyDriver"
      | "desktopArtifactStrategyDriver"
      | "preparedRecording"
      | "preparedRecordingTimeout"
      | "attachRecoveredDesktopRecordingState"
      | "getDesktopRecordingRecoveryState"
      | "logMissingRecoveredDesktopRecording"
      | "rehydrateActiveDesktopRecordingState"
      | "getStrategyDriver"
      | "getStrategyDriverForRecording"
      | "createFreshController"
      | "createRecoveredDesktopController"
      | "getDesktopControllerKind"
      | "shouldUseDesktopWebArtifactController"
      | "PREPARED_RECORDING_TIMEOUT"
    >(this, {
      lastError: observable,
      isPaused: observable,
      visualizer: observable.ref,
      waveformSamples: observable.ref,
      isMicrophoneDisconnected: observable,
      activeAudioInputDeviceLabel: observable,
      isAudioSilent: observable,
      audioSilentSince: observable,
      isInitializing: observable,
      audioRecordingStateMap: observable.shallow,
      transcriptionStatusByRecording: observable.shallow,
      liveTranscriptPartialsByRecording: observable.shallow,
      liveDurationMsByRecording: observable.shallow,
      silenceSnapshotByRecording: false,
      getSilenceSnapshotForRecording: false,
      preparedRecordingController: observable,
      webArtifactStrategyDriver: false,
      desktopArtifactStrategyDriver: false,
      preparedRecording: false,
      preparedRecordingTimeout: false,
      debugActiveRecordings: computed,
      start: action.bound,
      pause: action.bound,
      resume: action.bound,
      stop: action.bound,
      clear: action.bound,
      ui: observable,
      onRecordingCompleted: observable,
      audioRecordings: computed,
      localAudioRecordingTranscriptTurns: computed,
      computeInitialIndexes: action,
      getPreparedRecording: action,
      refreshPreparedRecording: action,
      createPreparedRecordingController: action,
      buildControllerEventHandlers: action,
      trackAudioChunkPersistence: action.bound,
      persistAudioChunk: action,
      persistTranscriptTurn: action,
      handleControllerError: action,
      handleControllerStop: action,
      buildRecordingCompletionDebugInfo: false,
      waitForUploadableArtifactReadiness: false,
      logRecordingCompletionSummary: false,
      disposeRecordingState: action,
      erroredRecordingIds: observable,
      markRecordingAsErrored: action,
      buildRecordingErrorMetadata: false,
      normalizeRecordingErrorDetails: false,
      buildDesktopRecordingStartFailureMetadata: false,
      buildDesktopStartFailureLogInfo: false,
      handleDesktopStartFallback: action,
      retryDesktopStartAfterSdkRestart: action,
      resolveDesktopRecordingStartAttemptTrace: false,
      buildDesktopRecordingStartPermissionErrorDetails: false,
      buildDesktopRecordingStartFailureDetails: false,
      buildRecallSdkErrorDetails: false,
      createDesktopRecordingStartAttemptTrace: false,
      resetState: action,
      initializeStateForRecording: action,
      setMicrophoneState: action,
      setVisualizer: action,
      setTranscriptionUnsupported: action,
      normalizeTranscriptTurnOrder: false,
      transcriptIsUnsupported: observable,
      autoStopManager: observable,
      silenceDetectionManager: observable,
      getTranscriptionStatus: action,
      clearTranscriptionStatus: action,
      setTranscriptionStatus: action,
      getLiveTranscriptPartials: false,
      upsertLiveTranscriptPartial: action.bound,
      removeLiveTranscriptPartial: action.bound,
      reconcileLiveTranscriptPartialsWithPersistedTurns: action.bound,
      clearLiveTranscriptPartials: action.bound,
      attachRecoveredDesktopRecordingState: action,
      getDesktopRecordingRecoveryState: false,
      logMissingRecoveredDesktopRecording: false,
      rehydrateActiveDesktopRecordingState: action,
      getPersistedDurationMs: action,
      getLiveDurationMs: false,
      setLiveDurationMs: action,
      updateLiveDurationFromWaveform: action,
      getRecordingStrategyForRecording: false,
      getStrategyDriver: false,
      getStrategyDriverForRecording: false,
      createFreshController: false,
      createRecoveredDesktopController: false,
      getDesktopControllerKind: false,
      shouldUseDesktopWebArtifactController: false,
      PREPARED_RECORDING_TIMEOUT: false,
      useInitializeAudioRecordingEffects: false,
    });

    this.preparedRecordingController = this.createPreparedRecordingController();

    this.autoStopManager = new AudioRecordingAutoStopManager({
      uiStore: this.ui,
      stopRecording: async ({ audioRecordingId }) => {
        // Use the full "finish" flow (clears active-recording state + releases the tab lock).
        await this.store.audio.finishRecording({ audioRecordingId });
      },
      getPersistedDurationMs: async audioRecordingId =>
        this.getPersistedDurationMs(audioRecordingId),
      isRecordingActive: audioRecordingId => this.audioRecordingStateMap.has(audioRecordingId),
    });

    this.silenceDetectionManager = new AudioRecordingSilenceDetectionManager({
      onStateChange: ({ isSilent, silentSince }) => {
        runInAction(() => {
          this.isAudioSilent = isSilent;
          this.audioSilentSince = silentSince;
        });
      },
    });
  }

  get debugActiveRecordings(): Array<{
    audioRecordingId: Uuid;
    hasController: boolean;
    initialTranscriptTurnOrder: number;
  }> {
    return Array.from(this.audioRecordingStateMap.entries()).map(([audioRecordingId, state]) => ({
      audioRecordingId,
      hasController: Boolean(state.controller),
      initialTranscriptTurnOrder: state.initialTranscriptTurnOrder,
    }));
  }

  private get audioRecordings(): AudioRecordingRepository {
    return this.store.dexie.repositories.audioRecordings;
  }

  private get localAudioRecordingTranscriptTurns(): LocalAudioRecordingTranscriptTurnsRepository {
    return this.store.dexie.repositories.localAudioRecordingTranscriptTurns;
  }

  private getStrategyDriver({
    recordingStrategy,
  }: {
    recordingStrategy: AudioRecordingStrategy;
  }): AudioRecordingStrategyDriver {
    if (recordingStrategy === AudioRecordingStrategy.DesktopArtifact) {
      return this.desktopArtifactStrategyDriver;
    }

    return this.webArtifactStrategyDriver;
  }

  private getStrategyDriverForRecording({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): AudioRecordingStrategyDriver {
    return this.getStrategyDriver({
      recordingStrategy: this.getRecordingStrategyForRecording({ audioRecordingId }),
    });
  }

  /**
   * Creates the live media controller for a new recording session.
   *
   * Recording strategy answers where durable audio lives. Controller choice is
   * a separate runtime decision:
   * - `DesktopArtifact` always records through the Electron-owned controller
   * - `WebArtifact` can still use the desktop media controller on supported
   *   desktop platforms while persisting into the web artifact flow
   */
  private createFreshController({
    customVocabulary,
    eventHandlers,
    forceWebMediaController = false,
    preparedRecording,
    recordingStrategy,
  }: {
    customVocabulary: string[];
    eventHandlers: MediaControllerEventHandlers;
    forceWebMediaController?: boolean;
    preparedRecording: PreparedRecording;
    recordingStrategy: AudioRecordingStrategy;
  }): MediaController {
    if (recordingStrategy === AudioRecordingStrategy.DesktopArtifact) {
      return new DesktopArtifactMediaController({
        eventHandlers,
        preparedRecording,
      });
    }

    if (this.shouldUseDesktopWebArtifactController({ forceWebMediaController })) {
      return new DesktopMediaController({
        eventHandlers,
        preparedRecording,
        recordingStrategy: DesktopAudioRecordingStrategy.WebArtifact,
      });
    }

    return new WebMediaController({
      customVocabulary,
      eventHandlers,
    });
  }

  /**
   * Reattaches to a live desktop session after the renderer lost its in-memory
   * controller state.
   *
   * Recovered sessions always use a desktop controller because the live audio
   * stream already exists in the desktop runtime at that point.
   */
  private createRecoveredDesktopController({
    eventHandlers,
    recordingStrategy,
  }: {
    eventHandlers: MediaControllerEventHandlers;
    recordingStrategy: AudioRecordingStrategy;
  }): MediaController {
    if (recordingStrategy === AudioRecordingStrategy.DesktopArtifact) {
      return new DesktopArtifactMediaController({
        eventHandlers,
      });
    }

    return new DesktopMediaController({
      eventHandlers,
      preparedRecording: null,
      recordingStrategy: DesktopAudioRecordingStrategy.WebArtifact,
    });
  }

  /**
   * `WebArtifact` recordings still prefer the desktop media controller on
   * supported desktop platforms so we keep system-audio capture quality while
   * storing the result in the web artifact path.
   */
  private shouldUseDesktopWebArtifactController({
    forceWebMediaController = false,
  }: {
    forceWebMediaController?: boolean;
  }): boolean {
    return (
      this.store.interface.isDesktop &&
      !this.store.electron.isIntelMacDesktop &&
      !forceWebMediaController
    );
  }

  async start({
    preparedRecording,
    detectedMeetingWindowId,
    calendarEventId,
    forceWebMediaController = false,
  }: {
    preparedRecording: PreparedRecording;
    detectedMeetingWindowId?: string;
    calendarEventId?: string;
    forceWebMediaController?: boolean;
  }): Promise<void> {
    const audioRecordingId = preparedRecording.audioRecordingId;
    const hadExistingState = this.audioRecordingStateMap.has(audioRecordingId);
    if (!hadExistingState) {
      runInAction(() => {
        this.isInitializing = true;
      });
    }
    const startAttemptTrace = this.createDesktopRecordingStartAttemptTrace();

    await this.initializeStateForRecording({
      preparedRecording,
      detectedMeetingWindowId,
      calendarEventId,
      forceWebMediaController,
      startAttemptTrace,
    });

    if (this.audioRecordingStateMap.has(audioRecordingId)) {
      await this.autoStopManager.beginMonitoring({ audioRecordingId });
    } else if (!hadExistingState) {
      runInAction(() => {
        this.isInitializing = false;
      });
    }
  }

  async pause({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    const state = this.audioRecordingStateMap.get(audioRecordingId);

    if (!state) {
      return;
    }

    await state.controller.pause();

    runInAction(() => {
      this.isPaused = true;
    });

    await this.audioRecordings.markAsPaused({ id: audioRecordingId });
  }

  async resume({ preparedRecording }: { preparedRecording: PreparedRecording }): Promise<void> {
    const audioRecordingId = preparedRecording.audioRecordingId;
    let state = this.audioRecordingStateMap.get(audioRecordingId);
    let shouldResumeController = Boolean(state);

    if (!state) {
      const recoveryState = await this.getDesktopRecordingRecoveryState(audioRecordingId);
      if (recoveryState?.kind === DesktopRecordingRecoveryKind.LiveSession) {
        const audioRecording = await this.audioRecordings.getById({ id: audioRecordingId });

        if (!audioRecording) {
          this.logMissingRecoveredDesktopRecording({
            action: "resume",
            audioRecordingId,
            level: "error",
            recoveryState,
          });
          throw new Error(`Missing recovered desktop recording ${audioRecordingId}`);
        }

        await this.attachRecoveredDesktopRecordingState({
          audioRecording,
          recoveryState,
          shouldBeginMonitoring: false,
        });
        shouldResumeController = true;
      } else {
        await this.initializeStateForRecording({
          preparedRecording,
        });
        shouldResumeController = false;
      }

      state = this.audioRecordingStateMap.get(audioRecordingId);
      if (!state) {
        throw new Error(`Failed to initialize audio recording state for ${audioRecordingId}`);
      }
    }

    try {
      if (shouldResumeController) {
        await state.controller.resume();
      }
      await this.autoStopManager.beginMonitoring({ audioRecordingId });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error({
        message: "[AppStoreAudioRecordingStore] resume failure",
        info: { error: errorMessage },
      });
      void this.audioRecordings.markAsResumingErrored({
        id: audioRecordingId,
        errorMessage,
      });
      throw error;
    }

    runInAction(() => {
      this.isPaused = false;
    });

    // Get the recall sdk upload id from the prepared recording credentials
    const recallUploadCredentials = await preparedRecording.recallUploadCredentials.catch(error => {
      logger.warn({
        message: "[AppStoreAudioRecordingStore] Failed to get recall upload credentials on resume",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
      return null;
    });
    await this.audioRecordings.markAsResumed({
      id: audioRecordingId,
      recallSdkUploadId: recallUploadCredentials?.sdkUploadId,
    });
  }

  /**
   * Stops the underlying media controller and finalizes the recording.
   *
   * Prefer calling `store.audio.finishRecording(...)` for user-facing flows so we also clear
   * active-recording state and release the cross-tab lock.
   */
  async stop({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    let state = this.audioRecordingStateMap.get(audioRecordingId);
    const persistedAudioRecording = await this.audioRecordings.getById({ id: audioRecordingId });
    const recordingStrategy = persistedAudioRecording
      ? getAudioRecordingStrategy({ modelData: persistedAudioRecording.data })
      : this.getRecordingStrategyForRecording({ audioRecordingId });
    const recordingStrategyDriver = this.getStrategyDriver({
      recordingStrategy,
    });
    let completedDurationMs: number | null = null;

    runInAction(() => {
      this.isPaused = false;
      this.isInitializing = false;
    });

    let didStopSuccessfully = false;

    /**
     * In some cases, they may not have a state (eg. if they just refreshed the page).
     */
    if (!state) {
      const recoveryState = await this.getDesktopRecordingRecoveryState(audioRecordingId);

      if (recoveryState?.kind === DesktopRecordingRecoveryKind.LiveSession) {
        const audioRecording = await this.audioRecordings.getById({ id: audioRecordingId });

        if (!audioRecording) {
          this.logMissingRecoveredDesktopRecording({
            action: "stop",
            audioRecordingId,
            level: "error",
            recoveryState,
          });
          throw new Error(`Missing recovered desktop recording ${audioRecordingId}`);
        }

        await this.attachRecoveredDesktopRecordingState({
          audioRecording,
          recoveryState,
          shouldBeginMonitoring: false,
        });
        state = this.audioRecordingStateMap.get(audioRecordingId);

        if (!state) {
          throw new Error(`Failed to attach recovered desktop recording ${audioRecordingId}`);
        }
      }
    }

    if (state) {
      try {
        await state.controller.stop();
        await state.pendingAudioChunkPersistence;
        completedDurationMs = await recordingStrategyDriver.completeRecording({
          audioRecordingId,
        });
        didStopSuccessfully = true;
      } catch (error) {
        logger.error({
          message: "[AppStoreAudioRecordingStore] stop failure",
          info: { error: error instanceof Error ? error.message : String(error) },
        });
        this.handleControllerError(audioRecordingId, { error });
      } finally {
        this.disposeRecordingState(audioRecordingId);
      }

      const hadControllerError = this.erroredRecordingIds.has(audioRecordingId);
      this.erroredRecordingIds.delete(audioRecordingId);

      if (!didStopSuccessfully || hadControllerError) {
        logger.warn({
          message: "[AppStoreAudioRecordingStore] stop failure",
          info: { audioRecordingId, didStopSuccessfully, hadControllerError },
        });

        return;
      }
    }

    if (completedDurationMs === null) {
      completedDurationMs = await recordingStrategyDriver.completeRecording({
        audioRecordingId,
      });
    }

    await this.waitForUploadableArtifactReadiness({
      audioRecordingId,
      recordingStrategy,
      recordingStrategyDriver,
    });

    await this.store.audioRecordings.getAsync(audioRecordingId);
    let recordingCompletionDebugInfo: RecordingCompletionDebugInfo | null = null;

    try {
      recordingCompletionDebugInfo = await this.buildRecordingCompletionDebugInfo({
        audioRecordingId,
        recordingStrategy,
      });
    } catch (error) {
      logger.warn({
        message: "[AppStoreAudioRecordingStore] Failed to collect recording completion debug info",
        info: { error: error instanceof Error ? error.message : String(error), audioRecordingId },
      });
    }

    // Capture the final silence probe verdict before teardown. We keep the
    // full snapshot in memory for the current session and also persist a small
    // summary on the completed-local app event so boot recovery can still
    // classify long silent recordings after a refresh.
    const finalizeSilenceSnapshot = this.silenceDetectionManager.getSnapshot();

    await this.audioRecordings.markAsRecordingCompleted({
      id: audioRecordingId,
      durationMs: completedDurationMs,
      recordingStrategy: recordingCompletionDebugInfo?.recordingStrategy,
      transcriptTurnCount: recordingCompletionDebugInfo?.transcriptTurnCount,
      artifactSummary: recordingCompletionDebugInfo?.artifactSummary,
      silenceSnapshot: finalizeSilenceSnapshot,
    });

    this.logRecordingCompletionSummary({
      audioRecordingId,
      recordingStrategy,
      recordingCompletionDebugInfo,
    });

    this.silenceSnapshotByRecording.set(audioRecordingId, finalizeSilenceSnapshot);

    this.onRecordingCompleted?.({ audioRecordingId });
  }

  /**
   * Collects a summary of the finalized local artifact plus transcript state so we can
   * persist and log the exact completion shape that later upload failures should inherit.
   */
  private async buildRecordingCompletionDebugInfo({
    audioRecordingId,
    recordingStrategy,
  }: {
    audioRecordingId: Uuid;
    recordingStrategy: AudioRecordingStrategy;
  }): Promise<RecordingCompletionDebugInfo> {
    const recordingStrategyDriver = this.getStrategyDriver({
      recordingStrategy,
    });
    const [artifactSummary, transcripts] = await Promise.all([
      recordingStrategyDriver.getArtifactDebugSummary({
        audioRecordingId,
      }),
      this.localAudioRecordingTranscriptTurns.getAllByRecordingId({
        associatedAudioRecordingId: audioRecordingId,
      }),
    ]);

    return {
      recordingStrategy,
      transcriptTurnCount: transcripts.length,
      artifactSummary,
    };
  }

  /**
   * Web-artifact recordings can transiently read as "missing" immediately
   * after finalize even though the finalized manifest/chunks are already being
   * committed to IndexedDB. Wait briefly for the uploadable ref to become
   * observable before handing the recording off to the pipeline.
   */
  private async waitForUploadableArtifactReadiness({
    audioRecordingId,
    recordingStrategy,
    recordingStrategyDriver,
  }: {
    audioRecordingId: Uuid;
    recordingStrategy: AudioRecordingStrategy;
    recordingStrategyDriver: AudioRecordingStrategyDriver;
  }): Promise<void> {
    if (recordingStrategy !== AudioRecordingStrategy.WebArtifact) {
      return;
    }

    const deadlineMs = Date.now() + WEB_ARTIFACT_READINESS_MAX_WAIT_MS;

    while (true) {
      const artifactStatus = await recordingStrategyDriver.getUploadableArtifactStatus({
        audioRecordingId,
      });

      if (artifactStatus.hasUploadableArtifact) {
        return;
      }

      if (Date.now() >= deadlineMs) {
        logger.warn({
          message:
            "[AppStoreAudioRecordingStore] uploadable web artifact was not observable after finalize",
          info: {
            audioRecordingId,
            artifactSizeBytes: artifactStatus.artifactSizeBytes,
            waitMs: WEB_ARTIFACT_READINESS_MAX_WAIT_MS,
          },
        });
        return;
      }

      await new Promise(resolve => setTimeout(resolve, WEB_ARTIFACT_READINESS_POLL_INTERVAL_MS));
    }
  }

  /**
   * Logs a summary of the recording from the database perspective.
   * This provides a second level of visibility alongside the WebMediaRecorder stats.
   */
  private logRecordingCompletionSummary({
    audioRecordingId,
    recordingStrategy,
    recordingCompletionDebugInfo,
  }: {
    audioRecordingId: Uuid;
    recordingStrategy: AudioRecordingStrategy;
    recordingCompletionDebugInfo: RecordingCompletionDebugInfo | null;
  }): void {
    if (!recordingCompletionDebugInfo) {
      logger.info({
        message: "[AppStoreAudioRecordingStore] Recording completed",
        info: {
          audioRecordingId,
          recordingStrategy,
          artifactSummaryUnavailable: true,
          userAgent: getRecordingCompletionUserAgent(),
        },
      });
      return;
    }

    const {
      artifactSummary,
      recordingStrategy: resolvedRecordingStrategy,
      transcriptTurnCount,
    } = recordingCompletionDebugInfo;
    const summary = {
      audioRecordingId,
      recordingStrategy: resolvedRecordingStrategy,
      localArtifactPartCount: artifactSummary.partCount,
      totalBytes: artifactSummary.sizeBytes,
      totalDurationSec: Math.round(artifactSummary.durationMs / 1000),
      totalDurationFormatted: Duration.fromMillis(artifactSummary.durationMs).toFormat("h:mm:ss"),
      transcriptTurnCount,
      artifactState: artifactSummary.state ?? null,
      artifactAppendCount: artifactSummary.appendCount ?? null,
      artifactBytesWritten: artifactSummary.bytesWritten ?? null,
      artifactFirstAppendAt: artifactSummary.firstAppendAt ?? null,
      artifactLastAppendAt: artifactSummary.lastAppendAt ?? null,
      artifactFinalizedAt: artifactSummary.finalizedAt ?? null,
      artifactFilePath: artifactSummary.filePath ?? null,
      recallSdkStateAtStart: artifactSummary.recallSdkStateAtStart ?? null,
      recallPermissionsAtStart: artifactSummary.recallPermissionsAtStart ?? null,
      userAgent: getRecordingCompletionUserAgent(),
    };

    if (artifactSummary.sizeBytes <= 0) {
      logger.warn({
        message: "[AppStoreAudioRecordingStore] Recording completed with no local audio data",
        info: summary,
      });
      return;
    }

    logger.info({
      message: "[AppStoreAudioRecordingStore] Recording completed",
      info: summary,
    });
  }

  async clear({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    this.autoStopManager.endMonitoring({ audioRecordingId });

    const state = this.audioRecordingStateMap.get(audioRecordingId);
    if (state) {
      try {
        await state.controller.stop?.();
      } catch (error) {
        logger.warn({
          message: "[AppStoreAudioRecordingStore] clear stop failure",
          info: { error: error instanceof Error ? error.message : String(error) },
        });
      }
      this.disposeRecordingState(audioRecordingId);
    }

    try {
      const recordingStrategyDriver = this.getStrategyDriverForRecording({
        audioRecordingId,
      });

      await Promise.all([
        recordingStrategyDriver.removeLocalArtifacts({
          audioRecordingId,
        }),
        this.localAudioRecordingTranscriptTurns.deleteAllByRecordingId({
          associatedAudioRecordingId: audioRecordingId,
        }),
      ]);
      this.clearTranscriptionStatus(audioRecordingId);
    } catch (error) {
      logger.error({
        message: "[AppStoreAudioRecordingStore] clear failure",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
    } finally {
      runInAction(() => {
        this.ui.resetRecording();
        this.isPaused = false;
        this.isInitializing = false;
        this.setVisualizer(null);
        this.waveformSamples = null;
        this.setMicrophoneState({ label: null, isDisconnected: false });
        this.clearTranscriptionStatus(audioRecordingId);
      });
    }
  }

  private async initializeStateForRecording({
    preparedRecording,
    detectedMeetingWindowId,
    calendarEventId,
    forceWebMediaController,
    allowRestartRetry = true,
    initializationTimeoutMs,
    startAttemptTrace,
  }: {
    preparedRecording: PreparedRecording;
    detectedMeetingWindowId?: string;
    calendarEventId?: string;
    forceWebMediaController?: boolean;
    allowRestartRetry?: boolean;
    initializationTimeoutMs?: number;
    startAttemptTrace?: DesktopRecordingStartAttemptTrace;
  }): Promise<void> {
    const audioRecordingId = preparedRecording.audioRecordingId;
    if (this.audioRecordingStateMap.has(audioRecordingId)) {
      return;
    }

    const audioRecording = await this.audioRecordings.getById({ id: audioRecordingId });
    if (!audioRecording) {
      this.logMissingRecoveredDesktopRecording({
        action: "rehydrate",
        audioRecordingId,
        level: "info",
      });
      return;
    }

    const recordingStrategy = this.getRecordingStrategyForRecording({ audioRecordingId });
    const recordingStrategyDriver = this.getStrategyDriver({
      recordingStrategy,
    });
    const { initialAudioChunkCount, initialTranscriptTurnOrder } = await this.computeInitialIndexes(
      {
        audioRecordingId,
        recordingStrategy,
      }
    );
    const controller = this.createFreshController({
      customVocabulary: toJS(this.store.spaceAccountSettings.customVocabulary),
      eventHandlers: this.buildControllerEventHandlers(audioRecordingId),
      forceWebMediaController,
      preparedRecording,
      recordingStrategy,
    });
    const desktopControllerKind = this.getDesktopControllerKind(controller);
    // Check if recording has existing audio chunks, transcripts or is in IN_PROGRESS status,
    // which indicates it was previously recording and is paused.
    const hasExistingAudioChunks = initialAudioChunkCount > 0;
    const hasExistingTranscript = initialTranscriptTurnOrder > 0;
    const isInProgress =
      audioRecording.data.recording_status === AudioRecordingRecordingStatus.IN_PROGRESS;
    const wasPaused =
      this.isPaused || hasExistingAudioChunks || hasExistingTranscript || isInProgress;
    const noteId = audioRecording.data.associated_note_id ?? null;
    const forceWebMediaControllerValue = forceWebMediaController ?? false;
    const desktopStartContext: DesktopStartFallbackContext = {
      audioRecordingId,
      calendarEventId,
      controllerKind: desktopControllerKind,
      detectedMeetingWindowId,
      forceWebMediaController: forceWebMediaControllerValue,
      noteId,
      recordingStrategy,
      startAttemptTrace,
      wasPaused,
    };
    const initializeOptions: MediaControllerInitializeOptions = {
      audioRecordingId: audioRecording.id,
      calendarEventId,
      noteId,
      detectedMeetingWindowId,
      initializationTimeoutMs,
      startAttemptTrace,
      onBeginRecording: () => {
        this.ui.beginRecording();
      },
      onFallbackToWebMedia: async ({ error }) =>
        this.handleDesktopStartFallback({
          ...desktopStartContext,
          error,
        }),
    };

    try {
      await recordingStrategyDriver.ensureLocalRecordingStarted({
        audioRecordingId,
      });

      await controller.initializeAndStart(initializeOptions);

      const controllerVisualizer = controller.getVisualizer?.() ?? null;

      runInAction(() => {
        this.lastError = null;
        this.setVisualizer(controllerVisualizer);
        this.waveformSamples = null;
        this.isMicrophoneDisconnected = false;
        this.activeAudioInputDeviceLabel = null;
        this.isPaused = false;
        this.isInitializing = false;
        this.setTranscriptionStatus(audioRecordingId, TranscriptionStatus.Live);
      });

      this.audioRecordingStateMap.set(audioRecordingId, {
        controller,
        initialTranscriptTurnOrder,
        pendingAudioChunkPersistence: Promise.resolve(),
      });

      // Post-start metadata — uses the current preparedRecording (not a stale closure).
      const recallUploadCredentials = await preparedRecording.recallUploadCredentials.catch(
        error => {
          logger.warn({
            message: "[AppStoreAudioRecordingStore] Failed to get recall upload credentials",
            info: { error: error instanceof Error ? error.message : String(error) },
          });
          return null;
        }
      );
      await this.audioRecordings.markAsRecordingStarted({
        id: audioRecording.id,
        recallSdkUploadId: recallUploadCredentials?.sdkUploadId,
      });

      logger.info({
        message: "[AppStoreAudioRecordingStore] Recording started",
        info: {
          audioRecordingId: audioRecording.id,
          noteId: audioRecording.data.associated_note_id ?? null,
          recordingStrategy,
          ...buildDesktopRecordingStartAttemptLogInfo({
            startAttemptTrace,
          }),
        },
      });

      try {
        await ensureSourceForRecording({
          store: this.store,
          audioRecordingId: audioRecording.id,
          noteId: audioRecording.data.associated_note_id ?? null,
        });
      } catch (error) {
        logger.error({
          message: "[AppStoreAudioRecordingStore] Failed to ensure source for recording",
          info: {
            audioRecordingId: audioRecording.id,
            error: error instanceof Error ? error.message : String(error),
          },
        });
      }
    } catch (unknownErr) {
      controller.dispose();

      // SDK was restarted — rebuild the full intent with fresh credentials (once).
      // When retry is exhausted, re-throw so the outer wrapper routes to fallback UX.
      if (unknownErr instanceof DesktopRecordingRestartRequiredError) {
        if (!allowRestartRetry) {
          throw unknownErr;
        }
        await this.retryDesktopStartAfterSdkRestart({
          desktopStartContext,
          error: unknownErr,
        });
        return;
      }

      const parsedError = errorHandlingModule.parseError({ unknownError: unknownErr });

      logger.error({
        message: "[AppStoreAudioRecordingStore] initialize state for recording failure",
        info: { error: objectModule.safeErrorAsJson(parsedError) },
      });

      runInAction(() => {
        this.lastError = parsedError;
        this.ui.resetRecording();
        this.isPaused = wasPaused;
        this.isInitializing = false;
      });
    }
  }

  private async computeInitialIndexes({
    audioRecordingId,
    recordingStrategy,
  }: {
    audioRecordingId: Uuid;
    recordingStrategy: AudioRecordingStrategy;
  }): Promise<{
    initialAudioChunkCount: number;
    initialTranscriptTurnOrder: number;
  }> {
    const recordingStrategyDriver = this.getStrategyDriver({
      recordingStrategy,
    });
    const [initialAudioChunkCount, transcripts] = await Promise.all([
      recordingStrategyDriver.getInitialAudioChunkCount({
        audioRecordingId,
      }),
      this.localAudioRecordingTranscriptTurns.getAllByRecordingId({
        associatedAudioRecordingId: audioRecordingId,
      }),
    ]);

    if (initialAudioChunkCount === 0) {
      if (recordingStrategy === AudioRecordingStrategy.DesktopArtifact) {
        return {
          initialAudioChunkCount: 0,
          initialTranscriptTurnOrder: transcripts.length
            ? transcripts[transcripts.length - 1].transcriptTurnIndex + 1
            : 0,
        };
      }

      // Erase transcripts if there are no local audio chunks to avoid the inconsistency.
      // This scenario can happen if the page is reloaded after starting to record.
      if (transcripts.length > 0) {
        logger.info({
          message:
            "[AppStoreAudioRecordingStore] clearing transcripts due to missing local audio chunks",
          info: { audioRecordingId, transcriptsCount: transcripts.length },
        });
        await this.localAudioRecordingTranscriptTurns.deleteAllByRecordingId({
          associatedAudioRecordingId: audioRecordingId,
        });
      }
      return {
        initialAudioChunkCount: 0,
        initialTranscriptTurnOrder: 0,
      };
    }

    return {
      initialAudioChunkCount,
      initialTranscriptTurnOrder: transcripts.length
        ? transcripts[transcripts.length - 1].transcriptTurnIndex + 1
        : 0,
    };
  }

  async rehydrateActiveDesktopRecordingState({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid | null;
  }): Promise<void> {
    if (!audioRecordingId) {
      return;
    }

    const audioRecording = await this.audioRecordings.getById({ id: audioRecordingId });
    if (!audioRecording) {
      return;
    }

    const audioRecordingStrategy = getAudioRecordingStrategy({
      modelData: audioRecording.data,
    });
    const recoveryState = await this.getDesktopRecordingRecoveryState(audioRecordingId);

    if (!recoveryState) {
      return;
    }

    if (typeof recoveryState.durationMs === "number") {
      this.setLiveDurationMs({
        audioRecordingId,
        durationMs: recoveryState.durationMs,
      });
    }

    if (recoveryState.kind === DesktopRecordingRecoveryKind.LiveSession) {
      await this.attachRecoveredDesktopRecordingState({
        audioRecording,
        recoveryState,
      });
      return;
    }

    if (audioRecordingStrategy !== AudioRecordingStrategy.DesktopArtifact) {
      logger.error({
        message:
          "[AppStoreAudioRecordingStore] skipping unfinished desktop artifact recovery due to recording strategy mismatch",
        info: {
          audioRecordingId,
          recoveredRecordingStrategy: recoveryState.recordingStrategy,
          audioRecordingStrategy,
        },
      });
      return;
    }

    if (!this.audioRecordingStateMap.has(audioRecordingId)) {
      runInAction(() => {
        this.isPaused = true;
      });
    }
  }

  private async attachRecoveredDesktopRecordingState({
    audioRecording,
    recoveryState,
    shouldBeginMonitoring = recoveryState.sessionState === DesktopRecordingSessionState.Recording,
  }: {
    audioRecording: AudioRecordingRecord;
    recoveryState: LiveDesktopRecordingRecoveryState;
    shouldBeginMonitoring?: boolean;
  }): Promise<void> {
    const audioRecordingId = audioRecording.id;
    const audioRecordingStrategy = getAudioRecordingStrategy({
      modelData: audioRecording.data,
    });
    const recoveredRecordingStrategy = normalizeAudioRecordingStrategy({
      recordingStrategy: recoveryState.recordingStrategy,
    });

    if (audioRecordingStrategy !== recoveredRecordingStrategy) {
      throw new Error(
        `Recovered desktop recording ${audioRecordingId} recording strategy mismatch: expected ${audioRecordingStrategy}, got ${recoveryState.recordingStrategy}`
      );
    }

    if (this.audioRecordingStateMap.has(audioRecordingId)) {
      runInAction(() => {
        this.isPaused = recoveryState.sessionState === DesktopRecordingSessionState.Paused;
      });

      if (shouldBeginMonitoring) {
        await this.autoStopManager.beginMonitoring({ audioRecordingId });
      }

      return;
    }

    const { initialTranscriptTurnOrder } = await this.computeInitialIndexes({
      audioRecordingId,
      recordingStrategy: audioRecordingStrategy,
    });
    const controller = this.createRecoveredDesktopController({
      eventHandlers: this.buildControllerEventHandlers(audioRecordingId),
      recordingStrategy: recoveredRecordingStrategy,
    });
    const recoveredRecordingState: AudioRecordingState = {
      controller,
      initialTranscriptTurnOrder,
      pendingAudioChunkPersistence: Promise.resolve(),
    };

    try {
      if (!controller.initializeForRecoveredSession) {
        throw new Error(`Recovered desktop controller is missing recovery initialization`);
      }

      this.audioRecordingStateMap.set(audioRecordingId, recoveredRecordingState);

      await controller.initializeForRecoveredSession({
        audioRecordingId,
      });

      const controllerVisualizer = controller.getVisualizer?.() ?? null;

      runInAction(() => {
        this.lastError = null;
        this.setVisualizer(controllerVisualizer);
        this.waveformSamples = null;
        this.isMicrophoneDisconnected = false;
        this.activeAudioInputDeviceLabel = null;
        this.isPaused = recoveryState.sessionState === DesktopRecordingSessionState.Paused;
        this.isInitializing = false;
        this.setTranscriptionStatus(audioRecordingId, TranscriptionStatus.Live);
      });

      if (shouldBeginMonitoring) {
        await this.autoStopManager.beginMonitoring({ audioRecordingId });
      }
    } catch (unknownErr) {
      if (this.audioRecordingStateMap.get(audioRecordingId)?.controller === controller) {
        this.audioRecordingStateMap.delete(audioRecordingId);
      }
      controller.dispose();

      const parsedError = errorHandlingModule.parseError({ unknownError: unknownErr });

      logger.error({
        message: "[AppStoreAudioRecordingStore] attach recovered desktop recording failure",
        info: { error: objectModule.safeErrorAsJson(parsedError) },
      });

      runInAction(() => {
        this.lastError = parsedError;
        this.isPaused = recoveryState.sessionState === DesktopRecordingSessionState.Paused;
        this.isInitializing = false;
      });

      throw unknownErr;
    }
  }

  private async getDesktopRecordingRecoveryState(
    audioRecordingId: Uuid
  ): Promise<DesktopRecordingRecoveryState | null> {
    return electronModule.recall.getRecordingRecoveryState({
      audioRecordingId,
    });
  }

  private logMissingRecoveredDesktopRecording({
    action,
    audioRecordingId,
    level,
    recoveryState = null,
  }: {
    action: "rehydrate" | "resume" | "stop";
    audioRecordingId: Uuid;
    level: "info" | "error";
    recoveryState?: LiveDesktopRecordingRecoveryState | null;
  }): void {
    const message =
      action === "rehydrate"
        ? "[AppStoreAudioRecordingStore] skipping recovered desktop recording rehydrate because local row is missing"
        : "[AppStoreAudioRecordingStore] recovered desktop recording is missing local row";
    const info = {
      action,
      audioRecordingId,
      recoveredSessionState: recoveryState?.sessionState ?? null,
      recoveredRecordingStrategy: recoveryState?.recordingStrategy ?? null,
    };

    if (level === "info") {
      logger.info({
        message,
        info,
      });

      return;
    }

    logger.error({
      message,
      info,
    });
  }

  async getPersistedDurationMs(audioRecordingId: Uuid): Promise<number> {
    const audioRecording = await this.audioRecordings.getById({ id: audioRecordingId });
    const recordingStrategy = audioRecording
      ? getAudioRecordingStrategy({ modelData: audioRecording.data })
      : this.getRecordingStrategyForRecording({ audioRecordingId });
    const recordingStrategyDriver = this.getStrategyDriver({
      recordingStrategy,
    });

    if (recordingStrategy === AudioRecordingStrategy.DesktopArtifact) {
      const liveDurationMs = this.getLiveDurationMs({ audioRecordingId });
      if (liveDurationMs > 0) {
        return liveDurationMs;
      }

      const persistedDurationMs = await recordingStrategyDriver.getPersistedDurationMs({
        audioRecordingId,
      });
      if (persistedDurationMs !== null) {
        return persistedDurationMs;
      }

      const recording = this.store.audioRecordings.get(audioRecordingId);
      if (!recording) {
        return 0;
      }

      const durationResult = calculateRecordingDuration({
        modelData: recording.modelData,
      });

      if (!durationResult.success) {
        return 0;
      }

      return Math.max(0, Math.round(durationResult.duration.as("milliseconds")));
    }

    const persistedDurationMs = await recordingStrategyDriver.getPersistedDurationMs({
      audioRecordingId,
    });
    return persistedDurationMs ?? 0;
  }

  private createPreparedRecordingController(): PreparedRecordingController {
    if (this.store.interface.isDesktop) {
      /**
       * This feature isn't currently supported on Intel-based MacOS devices.
       * (Instead, we fall back to the web-based media controller.)
       */
      if (!this.store.electron.isIntelMacDesktop) {
        return new DesktopPreparedRecordingController();
      }
    }

    return new WebPreparedRecordingController();
  }

  getPreparedRecording(audioRecordingId?: Uuid): PreparedRecording {
    try {
      if (
        this.preparedRecording &&
        this.preparedRecording.expiresAt >= DateTime.local() &&
        (!audioRecordingId || this.preparedRecording.audioRecordingId === audioRecordingId)
      ) {
        return this.preparedRecording;
      }

      const customVocabulary = toJS(this.store.spaceAccountSettings.customVocabulary);
      return this.preparedRecordingController.prepareRecording({
        customVocabulary,
        audioRecordingId: audioRecordingId ?? uuidModule.generate(),
      });
    } finally {
      // Don't reuse the same prepared recording.
      this.refreshPreparedRecording();
    }
  }

  private readonly PREPARED_RECORDING_TIMEOUT = 3600_000; // 1 hour

  refreshPreparedRecording(): void {
    logger.info({
      message: "[AppStoreAudioRecordingStore] Refresh prepared recording",
    });
    clearTimeout(this.preparedRecordingTimeout);

    // Use active recording id.
    const audioRecordingId = this.store.audio.activeRecordingId;

    if (audioRecordingId && !this.isPaused) {
      return;
    }

    const customVocabulary = toJS(this.store.spaceAccountSettings.customVocabulary);
    this.preparedRecording = this.preparedRecordingController.prepareRecording({
      customVocabulary,
      audioRecordingId: audioRecordingId ?? uuidModule.generate(),
    });

    this.preparedRecordingTimeout = window.setTimeout(() => {
      // Refresh the prepared recording after 1 hour to avoid stale data.
      this.refreshPreparedRecording();
    }, this.PREPARED_RECORDING_TIMEOUT);
  }

  private buildControllerEventHandlers(audioRecordingId: Uuid): MediaControllerEventHandlers {
    return {
      onAudioChunk: ({ chunk }) => {
        this.trackAudioChunkPersistence({
          audioRecordingId,
          chunk,
        });
      },
      onTranscriptPartial: ({ partialTurn }) => {
        const normalizedPartialTurn = this.normalizeTranscriptTurnOrder({
          audioRecordingId,
          turn: partialTurn,
        });

        runInAction(() => {
          this.setTranscriptionUnsupported(false);
          this.setTranscriptionStatus(audioRecordingId, TranscriptionStatus.Live);
        });
        this.upsertLiveTranscriptPartial({
          audioRecordingId,
          partialTurn: normalizedPartialTurn,
        });
      },
      onTranscriptFinal: ({ finalTurn }) => {
        const normalizedFinalTurn = this.normalizeTranscriptTurnOrder({
          audioRecordingId,
          turn: finalTurn,
        });

        runInAction(() => {
          this.setTranscriptionUnsupported(false);
          this.setTranscriptionStatus(audioRecordingId, TranscriptionStatus.Live);
        });

        const persistFinalTranscriptTurn = this.persistTranscriptTurn({
          audioRecordingId,
          finalTurn: normalizedFinalTurn,
        });

        void persistFinalTranscriptTurn.catch(error => {
          logger.error({
            message: "[AppStoreAudioRecordingStore] transcript turn persistence failure",
            info: {
              audioRecordingId,
              error: error instanceof Error ? error.message : String(error),
            },
          });
        });
      },
      onWaveformSamples: ({ samples }) => {
        runInAction(() => {
          this.waveformSamples = samples;
        });

        if (typeof samples.elapsedDurationMs === "number") {
          this.updateLiveDurationFromWaveform({
            audioRecordingId,
            nextDurationMs: samples.elapsedDurationMs,
          });
        }
      },
      onError: ({ error }) => {
        this.handleControllerError(audioRecordingId, { error });
      },
      onStop: () => {
        this.handleControllerStop(audioRecordingId);
      },
      onTranscriptionUnavailable: () => {
        runInAction(() => {
          this.setTranscriptionStatus(audioRecordingId, TranscriptionStatus.Disconnected);
        });
      },
      onTranscriptionUnsupported: () => {
        runInAction(() => {
          this.setTranscriptionUnsupported(true);
        });
      },
      onTranscriptionStatusChange: ({ status }) => {
        runInAction(() => {
          this.setTranscriptionStatus(audioRecordingId, status);
        });
      },
      onVoiceDisconnected: () => {
        void this.audioRecordings.markVoiceDisconnected({ id: audioRecordingId });
      },
      onVoiceReconnected: () => {
        void this.audioRecordings.markVoiceReconnected({ id: audioRecordingId });
      },
      onDebugEvent: ({ data, kind }) => {
        this.store.electron.pushRecallDebugEvent(data, kind);
      },
    };
  }

  private trackAudioChunkPersistence({
    audioRecordingId,
    chunk,
  }: {
    audioRecordingId: Uuid;
    chunk: MediaRecorderAudioChunk;
  }): void {
    const state = this.audioRecordingStateMap.get(audioRecordingId);
    if (!state) {
      return;
    }

    const startedPersistence = this.persistAudioChunk({
      audioRecordingId,
      chunk,
    });

    void startedPersistence.catch(error => {
      logger.error({
        message: "[AppStoreAudioRecordingStore] audio chunk persistence failure",
        info: {
          audioRecordingId,
          error: error instanceof Error ? error.message : String(error),
        },
      });
    });

    const pendingAudioChunkPersistence = Promise.allSettled([
      state.pendingAudioChunkPersistence,
      startedPersistence,
    ]).then(results => {
      const rejectedResult = results.find(
        (result): result is PromiseRejectedResult => result.status === "rejected"
      );

      if (rejectedResult) {
        throw rejectedResult.reason;
      }
    });

    state.pendingAudioChunkPersistence = pendingAudioChunkPersistence;

    void pendingAudioChunkPersistence.catch(() => {});
  }

  private async persistAudioChunk({
    audioRecordingId,
    chunk,
  }: {
    audioRecordingId: Uuid;
    chunk: MediaRecorderAudioChunk;
  }): Promise<void> {
    const state = this.audioRecordingStateMap.get(audioRecordingId);
    if (!state) return;

    const recordingStrategy = this.getRecordingStrategyForRecording({ audioRecordingId });
    const recordingStrategyDriver = this.getStrategyDriver({
      recordingStrategy,
    });

    await recordingStrategyDriver.appendAudioChunk({
      audioRecordingId,
      chunk,
    });

    if (recordingStrategy === AudioRecordingStrategy.WebArtifact) {
      this.autoStopManager.handleChunk({
        audioRecordingId,
        durationMs: chunk.durationMs,
      });
    }
  }

  private async persistTranscriptTurn({
    audioRecordingId,
    finalTurn,
  }: {
    audioRecordingId: Uuid;
    finalTurn: MediaRecorderTranscriptFinalTurn;
  }): Promise<void> {
    const state = this.audioRecordingStateMap.get(audioRecordingId);
    if (!state) return;

    const transcriptTurnIndex = finalTurn.turnOrder;
    await this.localAudioRecordingTranscriptTurns.upsert({
      associatedAudioRecordingId: audioRecordingId,
      transcriptTurnIndex: transcriptTurnIndex,
      data: {
        channel: finalTurn.channel,
        content: {
          turnTimestamp: DateTime.fromMillis(finalTurn.timestampMs),
          turnOrder: finalTurn.turnOrder,
          isFormatted: false,
          isEndOfTurn: true,
          endOfTurnConfidence: 1,
          participantId: finalTurn.participantId ?? null,
          words: (finalTurn.words ?? []).map(word => ({
            text: word.word,
            start: word.startMs,
            end: word.endMs,
            confidence: 1,
          })),
        },
      },
    });
  }

  getLiveTranscriptPartials({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): MediaRecorderTranscriptPartialTurn[] {
    return (
      this.liveTranscriptPartialsByRecording.get(audioRecordingId) ?? EMPTY_LIVE_TRANSCRIPT_PARTIALS
    );
  }

  private normalizeTranscriptTurnOrder<
    T extends MediaRecorderTranscriptPartialTurn | MediaRecorderTranscriptFinalTurn,
  >({ audioRecordingId, turn }: { audioRecordingId: Uuid; turn: T }): T {
    const state = this.audioRecordingStateMap.get(audioRecordingId);
    const initialTranscriptTurnOrder = state?.initialTranscriptTurnOrder ?? 0;

    if (initialTranscriptTurnOrder === 0) {
      return turn;
    }

    return {
      ...turn,
      turnOrder: initialTranscriptTurnOrder + turn.turnOrder,
    };
  }

  private upsertLiveTranscriptPartial({
    audioRecordingId,
    partialTurn,
  }: {
    audioRecordingId: Uuid;
    partialTurn: MediaRecorderTranscriptPartialTurn;
  }): void {
    const currentPartials = this.liveTranscriptPartialsByRecording.get(audioRecordingId) ?? [];
    const nextPartials = [
      ...currentPartials.filter(turn => turn.turnOrder !== partialTurn.turnOrder),
      partialTurn,
    ].sort((leftTurn, rightTurn) => leftTurn.turnOrder - rightTurn.turnOrder);

    this.liveTranscriptPartialsByRecording.set(audioRecordingId, nextPartials);
  }

  private removeLiveTranscriptPartial({
    audioRecordingId,
    turnOrder,
  }: {
    audioRecordingId: Uuid;
    turnOrder: number;
  }): void {
    const currentPartials = this.liveTranscriptPartialsByRecording.get(audioRecordingId);
    if (!currentPartials) {
      return;
    }

    const nextPartials = currentPartials.filter(turn => turn.turnOrder !== turnOrder);
    if (!nextPartials.length) {
      this.liveTranscriptPartialsByRecording.delete(audioRecordingId);
      return;
    }

    this.liveTranscriptPartialsByRecording.set(audioRecordingId, nextPartials);
  }

  reconcileLiveTranscriptPartialsWithPersistedTurns({
    audioRecordingId,
    persistedTurnOrders,
  }: {
    audioRecordingId: Uuid;
    persistedTurnOrders: number[];
  }): void {
    if (!persistedTurnOrders.length) {
      return;
    }

    const currentPartials = this.liveTranscriptPartialsByRecording.get(audioRecordingId);
    if (!currentPartials?.length) {
      return;
    }

    const persistedTurnOrderSet = new Set(persistedTurnOrders);
    const nextPartials = currentPartials.filter(turn => !persistedTurnOrderSet.has(turn.turnOrder));

    if (nextPartials.length === currentPartials.length) {
      return;
    }

    if (!nextPartials.length) {
      this.liveTranscriptPartialsByRecording.delete(audioRecordingId);
      return;
    }

    this.liveTranscriptPartialsByRecording.set(audioRecordingId, nextPartials);
  }

  private clearLiveTranscriptPartials({ audioRecordingId }: { audioRecordingId: Uuid }): void {
    this.liveTranscriptPartialsByRecording.delete(audioRecordingId);
  }

  private buildRecordingErrorMetadata({ error }: { error: unknown }): RecordingErrorMetadata {
    const parsedError = errorHandlingModule.parseError({ unknownError: error });
    const audioRecordingError = parsedError instanceof AudioRecordingError ? parsedError : null;
    const rawError =
      audioRecordingError?.originalError ??
      parsedError.originalError ??
      (error instanceof Error ? error : null);
    const rawErrorDetails = rawError
      ? objectModule.safeErrorAsJson(rawError)
      : objectModule.safeAsJson({ error });
    const parsedErrorInfo =
      parsedError.info && Object.keys(parsedError.info).length > 0
        ? objectModule.safeAsJson(parsedError.info as Record<string, unknown>)
        : null;
    const errorDetails = {
      ...((audioRecordingError?.details as Record<string, unknown> | undefined) ?? {}),
      raw_error: rawErrorDetails,
      parsed_error_code: parsedError.code,
      parsed_error_status_code: parsedError.statusCode,
      parsed_error_info: parsedErrorInfo,
    };

    return {
      errorMessage: parsedError.message,
      errorName:
        error instanceof Error
          ? error.name
          : parsedError instanceof Error
            ? parsedError.name
            : null,
      errorSource: audioRecordingError?.source ?? null,
      errorStage: audioRecordingError?.stage ?? null,
      errorSummary: audioRecordingError?.summary ?? null,
      errorDetails: this.normalizeRecordingErrorDetails(errorDetails),
    };
  }

  private normalizeRecordingErrorDetails(
    errorDetails?: Record<string, unknown> | null
  ): Record<string, unknown> | null {
    const normalizedErrorDetails = objectModule.compactNullOrUndefined(errorDetails ?? {});
    return Object.keys(normalizedErrorDetails).length > 0 ? normalizedErrorDetails : null;
  }

  private buildDesktopStartFailureLogInfo({
    error,
    startAttemptTrace,
  }: {
    error: unknown;
    startAttemptTrace?: DesktopRecordingStartAttemptTrace;
  }): Record<string, unknown> {
    const startFailureError = getDesktopRecordingStartFailureError({
      error,
    });

    if (startFailureError) {
      return buildDesktopRecordingStartFailureLogInfo({
        error: startFailureError,
      });
    }

    return buildDesktopRecordingStartAttemptLogInfo({
      startAttemptTrace,
    });
  }

  private async handleDesktopStartFallback({
    audioRecordingId,
    calendarEventId,
    controllerKind,
    detectedMeetingWindowId,
    error,
    forceWebMediaController,
    noteId,
    recordingStrategy,
    startAttemptTrace,
    wasPaused,
  }: DesktopStartFallbackContext & {
    error: unknown;
  }): Promise<void> {
    const errorMetadata = this.buildDesktopRecordingStartFailureMetadata({
      controllerKind,
      error,
      recordingStrategy,
      forceWebMediaController,
      startAttemptTrace,
      wasPaused,
    });
    const startFailureMetadata = getDesktopRecordingStartFailureMetadata({
      error,
    });
    const resolvedStartAttemptTrace =
      startFailureMetadata?.startAttemptTrace ?? startAttemptTrace ?? null;

    logger.warn({
      message: "[AppStoreAudioRecordingStore] desktop start failed",
      info: objectModule.safeAsJson({
        audioRecordingId,
        noteId,
        calendarEventId: calendarEventId ?? null,
        controllerKind,
        detectedMeetingWindowId: detectedMeetingWindowId ?? null,
        failureReason: errorMetadata.failureReason,
        failurePhase: startFailureMetadata?.failurePhase ?? null,
        failureSource: startFailureMetadata?.failureSource ?? null,
        forceWebMediaController,
        recallSdkErrorKind:
          startFailureMetadata?.recallSdkErrorDiagnostics?.recallSdkErrorKind ?? null,
        recallSdkStatusCode:
          startFailureMetadata?.recallSdkErrorDiagnostics?.recallSdkStatusCode ?? null,
        recallSdkState: startFailureMetadata?.recallSdkState ?? null,
        recordingStrategy,
        restartSucceeded: startFailureMetadata?.restartSucceeded ?? null,
        uploadTokenResolved: startFailureMetadata?.uploadTokenResolved ?? null,
        wasPaused,
        windowIdResolved: startFailureMetadata?.windowIdResolved ?? null,
        ...buildDesktopRecordingStartAttemptLogInfo({
          startAttemptTrace: resolvedStartAttemptTrace,
        }),
      }),
    });

    logger.warn({
      message: "[AppStoreAudioRecordingStore] Auto-retry failed, showing error modal",
      info: objectModule.safeAsJson({
        audioRecordingId,
        noteId,
        calendarEventId: calendarEventId ?? null,
        detectedMeetingWindowId: detectedMeetingWindowId ?? null,
        ...this.buildDesktopStartFailureLogInfo({
          error,
          startAttemptTrace,
        }),
        ...errorMetadata,
      }),
    });

    await this.markRecordingAsErrored({
      audioRecordingId,
      ...errorMetadata,
    });

    if (wasPaused) {
      runInAction(() => {
        this.isPaused = true;
      });
    } else {
      logger.info({
        message: "[AppStoreAudioRecordingStore] Start recording failed, trashing...",
        info: { audioRecordingId },
      });
      void this.store.audio.trashRecording({
        audioRecordingId,
        context: { reason: TrashRecordingReason.StartRecordingFailed },
      });
    }

    this.ui.promptSystemAudioRecordingFailed({
      audioRecordingId,
      noteId,
      context: {
        reason: DesktopAudioRecorderFailureReason.DesktopRecorderStartFailed,
      },
      onConfirm: async () => {
        await this.store.audio.createAndStartAudioRecording({
          associatedNoteId: noteId,
          associatedMeetingWindowId: detectedMeetingWindowId,
          calendarEventId,
          config: {
            showOnboardingTooltip: false,
            forceWebMediaController: true,
          },
        });
      },
      onRetry: async () => {
        await this.store.audio.createAndStartAudioRecording({
          associatedNoteId: noteId,
          associatedMeetingWindowId: detectedMeetingWindowId,
          calendarEventId,
          config: {
            showOnboardingTooltip: false,
            forceWebMediaController: false,
          },
        });
      },
    });
  }

  private async retryDesktopStartAfterSdkRestart({
    desktopStartContext,
    error,
  }: {
    desktopStartContext: DesktopStartFallbackContext;
    error: DesktopRecordingRestartRequiredError;
  }): Promise<void> {
    const retryStartAttemptTrace = this.createDesktopRecordingStartAttemptTrace({
      previousTrace:
        desktopStartContext.startAttemptTrace ?? error.metadata?.startAttemptTrace ?? null,
    });

    logger.warn({
      message: "[AppStoreAudioRecordingStore] SDK restart required, rebuilding intent",
      info: objectModule.safeAsJson({
        audioRecordingId: desktopStartContext.audioRecordingId,
        ...buildDesktopRecordingRestartRequiredLogInfo({
          error,
        }),
      }),
    });

    try {
      const customVocabulary = toJS(this.store.spaceAccountSettings.customVocabulary);
      const freshPreparedRecording = this.preparedRecordingController.prepareRecording({
        customVocabulary,
        audioRecordingId: desktopStartContext.audioRecordingId,
      });
      await this.initializeStateForRecording({
        preparedRecording: freshPreparedRecording,
        detectedMeetingWindowId: desktopStartContext.detectedMeetingWindowId,
        calendarEventId: desktopStartContext.calendarEventId,
        forceWebMediaController: desktopStartContext.forceWebMediaController,
        allowRestartRetry: false,
        initializationTimeoutMs: 12_000,
        startAttemptTrace: retryStartAttemptTrace,
      });
    } catch (retryError) {
      logger.error({
        message: "[AppStoreAudioRecordingStore] retry after SDK restart also failed",
        info: objectModule.safeAsJson({
          audioRecordingId: desktopStartContext.audioRecordingId,
          error: retryError instanceof Error ? retryError.message : String(retryError),
          ...this.buildDesktopStartFailureLogInfo({
            error: retryError,
            startAttemptTrace: retryStartAttemptTrace,
          }),
        }),
      });

      await this.handleDesktopStartFallback({
        ...desktopStartContext,
        error: retryError instanceof Error ? retryError : new Error(String(retryError)),
        startAttemptTrace: retryStartAttemptTrace,
      });
    }
  }

  private resolveDesktopRecordingStartAttemptTrace({
    startFailureMetadata,
    startAttemptTrace,
  }: {
    startFailureMetadata?: DesktopRecordingStartFailureMetadata | null;
    startAttemptTrace?: DesktopRecordingStartAttemptTrace;
  }): DesktopRecordingStartAttemptTrace | null {
    return startFailureMetadata?.startAttemptTrace ?? startAttemptTrace ?? null;
  }

  private buildDesktopRecordingStartPermissionErrorDetails({
    recallPermissions,
  }: {
    recallPermissions?: RecallPermissionMap | null;
  }): Record<string, unknown> {
    return {
      recall_permission_accessibility:
        recallPermissions?.[RecallPermissionKind.Accessibility] ?? null,
      recall_permission_screen_capture:
        recallPermissions?.[RecallPermissionKind.ScreenCapture] ?? null,
      recall_permission_system_audio: recallPermissions?.[RecallPermissionKind.SystemAudio] ?? null,
      recall_permission_microphone: recallPermissions?.[RecallPermissionKind.Microphone] ?? null,
      recall_permission_full_disk_access:
        recallPermissions?.[RecallPermissionKind.FullDiskAccess] ?? null,
    };
  }

  private buildDesktopRecordingStartFailureDetails({
    controllerKind,
    recordingStrategy,
    startFailureMetadata,
    startAttemptTrace,
  }: {
    controllerKind: DesktopControllerKind | null;
    recordingStrategy: AudioRecordingStrategy;
    startFailureMetadata?: DesktopRecordingStartFailureMetadata | null;
    startAttemptTrace?: DesktopRecordingStartAttemptTrace;
  }): Record<string, unknown> {
    const resolvedStartAttemptTrace = this.resolveDesktopRecordingStartAttemptTrace({
      startFailureMetadata,
      startAttemptTrace,
    });

    return {
      controller_kind: controllerKind,
      controller_operation: "initialize_and_start",
      root_start_attempt_id: resolvedStartAttemptTrace?.rootStartAttemptId ?? null,
      start_attempt_id: resolvedStartAttemptTrace?.startAttemptId ?? null,
      attempt_number: resolvedStartAttemptTrace?.attemptNumber ?? null,
      failure_source: startFailureMetadata?.failureSource ?? null,
      failure_phase: startFailureMetadata?.failurePhase ?? null,
      timeout_ms: startFailureMetadata?.timeoutMs ?? null,
      note_id: startFailureMetadata?.noteId ?? null,
      detected_meeting_window_id: startFailureMetadata?.detectedMeetingWindowId ?? null,
      recording_strategy: startFailureMetadata?.recordingStrategy ?? recordingStrategy ?? null,
      window_id_resolved: startFailureMetadata?.windowIdResolved ?? null,
      upload_token_resolved: startFailureMetadata?.uploadTokenResolved ?? null,
      recall_sdk_state: startFailureMetadata?.recallSdkState ?? null,
      recall_sdk_error_message: startFailureMetadata?.recallSdkErrorMessage ?? null,
      restart_succeeded: startFailureMetadata?.restartSucceeded ?? null,
      restart_error: startFailureMetadata?.restartError ?? null,
      ...this.buildRecallSdkErrorDetails({
        diagnostics: startFailureMetadata?.recallSdkErrorDiagnostics ?? null,
      }),
      ...this.buildDesktopRecordingStartPermissionErrorDetails({
        recallPermissions: startFailureMetadata?.recallPermissions ?? null,
      }),
    };
  }

  private buildRecallSdkErrorDetails({
    diagnostics,
  }: {
    diagnostics?: RecallSdkErrorDiagnostics | null;
  }): Record<string, unknown> {
    return {
      recall_sdk_error_kind: diagnostics?.recallSdkErrorKind ?? null,
      recall_sdk_error_name: diagnostics?.recallSdkErrorName ?? null,
      recall_sdk_error_code: diagnostics?.recallSdkErrorCode ?? null,
      recall_sdk_error_type: diagnostics?.recallSdkErrorType ?? null,
      recall_sdk_status_code: diagnostics?.recallSdkStatusCode ?? null,
      recall_sdk_process_exit_code: diagnostics?.recallSdkProcessExitCode ?? null,
      recall_sdk_os_error_code: diagnostics?.recallSdkOsErrorCode ?? null,
      recall_sdk_os_error_kind: diagnostics?.recallSdkOsErrorKind ?? null,
    };
  }

  private buildDesktopRecordingStartFailureMetadata({
    controllerKind,
    error,
    recordingStrategy,
    forceWebMediaController,
    startAttemptTrace,
    wasPaused,
  }: {
    controllerKind: DesktopControllerKind | null;
    error: unknown;
    recordingStrategy: AudioRecordingStrategy;
    forceWebMediaController: boolean;
    startAttemptTrace?: DesktopRecordingStartAttemptTrace;
    wasPaused: boolean;
  }): RecordingErrorMetadata {
    const baseMetadata = this.buildRecordingErrorMetadata({ error });
    const startFailureMetadata = getDesktopRecordingStartFailureMetadata({
      error,
    });

    return {
      ...baseMetadata,
      errorDetails: this.normalizeRecordingErrorDetails({
        ...(baseMetadata.errorDetails ?? {}),
        ...this.buildDesktopRecordingStartFailureDetails({
          controllerKind,
          recordingStrategy,
          startFailureMetadata,
          startAttemptTrace,
        }),
      }),
      failureReason: DESKTOP_RECORDER_START_FAILURE_REASON,
      recordingStrategy,
      forceWebMediaController,
      wasPaused,
    };
  }

  private createDesktopRecordingStartAttemptTrace({
    previousTrace = null,
  }: {
    previousTrace?: DesktopRecordingStartAttemptTrace | null;
  } = {}): DesktopRecordingStartAttemptTrace {
    return {
      rootStartAttemptId: previousTrace?.rootStartAttemptId ?? uuidModule.generate(),
      startAttemptId: uuidModule.generate(),
      attemptNumber: (previousTrace?.attemptNumber ?? 0) + 1,
    };
  }

  private getDesktopControllerKind(controller: MediaController): DesktopControllerKind | null {
    return controller.kind === "desktop_media_controller" ||
      controller.kind === "desktop_artifact_media_controller"
      ? controller.kind
      : null;
  }

  private async markRecordingAsErrored({
    audioRecordingId,
    ...errorMetadata
  }: {
    audioRecordingId: Uuid;
  } & RecordingErrorMetadata): Promise<void> {
    try {
      await this.audioRecordings.markAsRecordingErrored({
        id: audioRecordingId,
        ...errorMetadata,
      });
    } catch (error) {
      logger.warn({
        message: "[AppStoreAudioRecordingStore] failed to mark recording as errored",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
    }
  }

  private handleControllerError(audioRecordingId: Uuid, { error }: { error: unknown }): void {
    const parsedError = errorHandlingModule.parseError({ unknownError: error });
    this.erroredRecordingIds.add(audioRecordingId);
    void this.markRecordingAsErrored({
      audioRecordingId,
      ...this.buildRecordingErrorMetadata({ error: parsedError }),
    });
    this.disposeRecordingState(audioRecordingId);
    runInAction(() => {
      this.lastError = parsedError;
      this.isPaused = false;
      this.isInitializing = false;
    });
  }

  private handleControllerStop(audioRecordingId: Uuid): void {
    this.autoStopManager.handleControllerStop({ audioRecordingId });

    this.disposeRecordingState(audioRecordingId);
    runInAction(() => {
      this.isPaused = false;
      this.isInitializing = false;
    });
  }

  private disposeRecordingState(audioRecordingId: Uuid): void {
    this.autoStopManager.endMonitoring({ audioRecordingId });
    this.liveDurationMsByRecording.delete(audioRecordingId);
    this.clearLiveTranscriptPartials({ audioRecordingId });

    const state = this.audioRecordingStateMap.get(audioRecordingId);
    if (!state) return;

    try {
      state.controller.dispose();
    } catch (error) {
      logger.warn({
        message: "[AppStoreAudioRecordingStore] dispose failure",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
    }

    this.audioRecordingStateMap.delete(audioRecordingId);

    runInAction(() => {
      this.setVisualizer(null);
      this.waveformSamples = null;
      this.isMicrophoneDisconnected = false;
      this.activeAudioInputDeviceLabel = null;
      this.setTranscriptionUnsupported(false);
      this.isInitializing = false;
    });
  }

  resetState() {
    this.audioRecordingStateMap.forEach(state => {
      try {
        state.controller.dispose();
      } catch (error) {
        logger.warn({
          message: "[AppStoreAudioRecordingStore] resetState dispose failure",
          info: { error: error instanceof Error ? error.message : String(error) },
        });
      }
    });
    this.audioRecordingStateMap.clear();
    this.erroredRecordingIds.clear();
    this.lastError = null;
    this.setTranscriptionUnsupported(false);
    this.transcriptionStatusByRecording.clear();
    this.liveTranscriptPartialsByRecording.clear();
    this.liveDurationMsByRecording.clear();
    this.silenceSnapshotByRecording.clear();
    this.autoStopManager.reset();
    this.silenceDetectionManager.reset();
    this.isInitializing = false;
  }

  setMicrophoneState({
    label,
    isDisconnected,
  }: {
    label?: string | null;
    isDisconnected?: boolean;
  }): void {
    if (label !== undefined) {
      this.activeAudioInputDeviceLabel = label;
    }

    if (typeof isDisconnected === "boolean") {
      this.isMicrophoneDisconnected = isDisconnected;
    }
  }

  setVisualizer(probe: AudioLevelProbe | null): void {
    if (this.visualizer === probe) {
      return;
    }

    if (this.visualizer && this.visualizer !== probe) {
      try {
        this.visualizer.dispose();
      } catch (error) {
        logger.warn({
          message: "[AppStoreAudioRecordingStore] setVisualizer dispose failure",
          info: { error: error instanceof Error ? error.message : String(error) },
        });
      }
    }

    this.visualizer = probe;
    this.silenceDetectionManager.attachProbe({ audioProbe: probe });
  }
  setTranscriptionUnsupported(value: boolean): void {
    this.transcriptIsUnsupported = value;
  }

  setTranscriptionStatus(audioRecordingId: Uuid, status: TranscriptionStatus): void {
    const current = this.transcriptionStatusByRecording.get(audioRecordingId);
    if (current === TranscriptionStatus.Compromised && status !== TranscriptionStatus.Compromised) {
      return;
    }

    // Track transcription disconnect/reconnect events
    const wasConnected = current === TranscriptionStatus.Live;
    const isNowDisconnected =
      status === TranscriptionStatus.Disconnected || status === TranscriptionStatus.Compromised;
    const isNowConnected = status === TranscriptionStatus.Live;
    const wasDisconnected =
      current === TranscriptionStatus.Disconnected || current === TranscriptionStatus.Compromised;

    if (wasConnected && isNowDisconnected) {
      void this.audioRecordings.markTranscriptionDisconnected({ id: audioRecordingId });
    } else if (wasDisconnected && isNowConnected) {
      void this.audioRecordings.markTranscriptionReconnected({ id: audioRecordingId });
    }

    // Track when transcription becomes compromised (after max reconnect attempts)
    if (status === TranscriptionStatus.Compromised && current !== TranscriptionStatus.Compromised) {
      void this.audioRecordings.markTranscriptionCompromised({ id: audioRecordingId });
    }

    this.transcriptionStatusByRecording.set(audioRecordingId, status);
  }

  getTranscriptionStatus(audioRecordingId: Uuid): TranscriptionStatus {
    // Check app_events first - compromised is a terminal/sticky state that takes precedence
    // This ensures we don't lose the compromised status after page refresh
    const recording = this.store.audioRecordings.get(audioRecordingId);
    if (recording?.wasTranscriptionCompromised) {
      return TranscriptionStatus.Compromised;
    }

    // Then check in-memory map for real-time status during active recording
    return this.transcriptionStatusByRecording.get(audioRecordingId) ?? TranscriptionStatus.Live;
  }

  clearTranscriptionStatus(audioRecordingId: Uuid): void {
    this.transcriptionStatusByRecording.delete(audioRecordingId);
  }

  getLiveDurationMs({ audioRecordingId }: { audioRecordingId: Uuid }): number {
    return this.liveDurationMsByRecording.get(audioRecordingId) ?? 0;
  }

  /**
   * Returns the silence-detection snapshot captured at recording finalize, or
   * null if this recording has not reached completion yet. Consumed by the
   * pipeline's validate-audio stage.
   */
  getSilenceSnapshotForRecording(audioRecordingId: Uuid): SilenceSnapshot | null {
    return this.silenceSnapshotByRecording.get(audioRecordingId) ?? null;
  }

  private setLiveDurationMs({
    audioRecordingId,
    durationMs,
  }: {
    audioRecordingId: Uuid;
    durationMs: number;
  }): void {
    this.liveDurationMsByRecording.set(audioRecordingId, Math.max(0, durationMs));
  }

  private updateLiveDurationFromWaveform({
    audioRecordingId,
    nextDurationMs,
  }: {
    audioRecordingId: Uuid;
    nextDurationMs: number;
  }): void {
    const previousDurationMs = this.getLiveDurationMs({ audioRecordingId });
    const normalizedDurationMs = Math.max(previousDurationMs, nextDurationMs);

    this.setLiveDurationMs({
      audioRecordingId,
      durationMs: normalizedDurationMs,
    });

    const deltaDurationMs = normalizedDurationMs - previousDurationMs;
    if (deltaDurationMs <= 0) {
      return;
    }

    this.autoStopManager.handleChunk({
      audioRecordingId,
      durationMs: deltaDurationMs,
    });
  }

  private getRecordingStrategyForRecording({
    audioRecordingId,
  }: {
    audioRecordingId: Uuid;
  }): AudioRecordingStrategy {
    return (
      this.store.audioRecordings.get(audioRecordingId)?.recordingStrategy ??
      AudioRecordingStrategy.WebArtifact
    );
  }

  useInitializeAudioRecordingEffects(): void {
    useEffectOnMount(() => {
      // Prepare for the first recording.
      this.refreshPreparedRecording();

      const reactionDisposers = [
        reaction(
          () => this.store.spaceAccountSettings.customVocabulary,
          () => {
            // Refresh the prepared recording when the custom vocabulary changes.
            this.refreshPreparedRecording();
          }
        ),
        reaction(
          () => this.store.audio.activeRecordingId,
          () => {
            // Refresh the prepared recording when the custom vocabulary changes.
            this.refreshPreparedRecording();
          }
        ),
      ];
      return () => reactionDisposers.forEach(f => f());
    });
  }
}
